import {useEffect, useState, useRef} from "react";
import {useLocation, useNavigate} from 'react-router-dom';
import {DataTable} from "../../components/data-table";
import {SiteHeader} from "../../components/site-header";
import {BinUsageStats} from "./bin-usage-stats";
import {Button} from "@/components/ui/button";
import {useFilters} from "@/common/Components/SideBar/Layout";
import axiosInstance from "@/api/axios";
import {HeatMap} from "@/components";
import {WasteComposition} from "@/components/waste-composition";
import {BinMediaViewer} from "@/components/bin-images-viewer";
import {DashboardExport} from "@/components/DashboardExport";
import {useTranslation} from 'react-i18next';
import {
    WasteStreamProduction,
    Stats,
    BinUsageRanking,
    FillLevelConsistency,
    PeakUsageTime,
    PeakFillLevel,
} from "../../components";
import SubclassDisplay from "../../components/Subclass-Display/SubclassDisplay";
import ContaminationChart from "../../components/ContaminationChart";
import WasteCreated from "@/pages/Others/Components/WasteCreated";
import {initializeApp} from '@firebase/app';
import {getMessaging, getToken, onMessage} from '@firebase/messaging';
import SubclassCardView from "../../components/Subclass-Display/SubclassCardView";

const firebaseConfig = {
    apiKey: "AIzaSyBylDQErfG_E2F7LpO6boLYf4QMRwtek3g",
    authDomain: "plaex-notifications-8dfa0.firebaseapp.com",
    projectId: "plaex-notifications-8dfa0",
    storageBucket: "plaex-notifications-8dfa0.firebasestorage.app",
    messagingSenderId: "16520628768",
    appId: "1:16520628768:web:f3f43ec41ebaa3ba8f8a45"
};

const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

// --- STORAGE KEYS ---
const STORAGE_KEY_BINS = "plaex_dashboard_selected_bins";
const STORAGE_KEY_TIME_RANGE = "plaex_dashboard_time_range";
const STORAGE_KEY_CUSTOM_DATE = "plaex_dashboard_custom_date";

// --- Helper function to generate user-specific storage key ---
const getStorageKey = (key, userId) => {
    const userPrefix = userId ? `user_${userId}_` : '';
    return `plaex_${userPrefix}${key}`;
};

// --- Helper function to get current user ID ---
const getCurrentUserId = () => {
    try {
        const userData = localStorage.getItem('userData');
        if (userData) {
            const parsed = JSON.parse(userData);
            return parsed.id || parsed.userId || null;
        }
        
        const sessionUser = sessionStorage.getItem('userId');
        if (sessionUser) return sessionUser;
        
        return null;
    } catch (error) {
        console.error('Error getting user ID:', error);
        return null;
    }
};

// --- Custom hook for persistent bin selection ---
const usePersistentBinSelection = (userId) => {
    const storageKey = userId ? getStorageKey('selected_bins', userId) : STORAGE_KEY_BINS;
    const hasLoadedFromStorage = useRef(false); // Track if we've loaded from storage
    
    // Get initial state from localStorage - ONLY ONCE
    const getStoredBins = () => {
        if (typeof window === 'undefined' || hasLoadedFromStorage.current) return [];
        
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                hasLoadedFromStorage.current = true; // Mark as loaded
                return JSON.parse(stored);
            }
        } catch (error) {
            console.error('Error reading from localStorage:', error);
        }
        hasLoadedFromStorage.current = true; // Mark as loaded even if empty
        return [];
    };

    const [selectedBins, setSelectedBins] = useState(() => {
        // Use lazy initial state to load from localStorage only once
        return getStoredBins();
    });

    // Save to localStorage whenever selectedBins changes
    useEffect(() => {
        if (selectedBins.length > 0 || hasLoadedFromStorage.current) {
            try {
                localStorage.setItem(storageKey, JSON.stringify(selectedBins));
            } catch (error) {
                console.error('Error saving to localStorage:', error);
            }
        }
    }, [selectedBins, storageKey]);

    // Toggle bin selection
    const toggleBinSelection = (binId) => {
        setSelectedBins(prev => {
            const isSelected = prev.includes(binId);
            if (isSelected) {
                return prev.filter(id => id !== binId);
            } else {
                return [...prev, binId];
            }
        });
        return false;
    };

    // Clear all bins
    const clearAllBins = () => {
        setSelectedBins([]);
    };

    // Select all bins
    const selectAllBins = (allBinIds) => {
        setSelectedBins(allBinIds);
    };

    // Update bins - with protection against resetting
    const updateSelectedBins = (bins) => {
        setSelectedBins(prev => {
            // Don't update if we already have a valid selection and new bins are empty/default
            if (prev.length > 0 && bins.length === 0) {
                return prev;
            }
            // Don't reset to default if we already have a selection
            if (prev.length > 0 && bins.length <= 3 && bins.every(bin => prev.includes(bin))) {
                return prev;
            }
            return bins;
        });
    };

    return {
        selectedBins,
        toggleBinSelection,
        clearAllBins,
        selectAllBins,
        updateSelectedBins
    };
};

export default function Page() {
    // Get current user ID
    const currentUserId = getCurrentUserId();
    
    // Use persistent bin selection hook
    const {
        selectedBins: persistentSelectedBins,
        toggleBinSelection: persistentToggleBinSelection,
        clearAllBins: persistentClearAllBins,
        selectAllBins: persistentSelectAllBins,
        updateSelectedBins: persistentUpdateSelectedBins
    } = usePersistentBinSelection(currentUserId);

    // State variables
    const [userRoleState, setUserRoleState] = useState(null);
    const [isLoadingRole, setIsLoadingRole] = useState(true);
    const [hasBinImages, setHasBinImages] = useState(false);
    const [binUsageIds, setBinUsageIds] = useState(null);
    const [showBinUsageStats, setShowBinUsageStats] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [refreshKey, setRefreshKey] = useState(0);
    const [localTimeRangeDropdownOpen, setLocalTimeRangeDropdownOpen] = useState(false);
    const [localBinDropdownOpen, setLocalBinDropdownOpen] = useState(false);
    
    // Load time range from localStorage
    const [localSelectedTimeRange, setLocalSelectedTimeRange] = useState(() => {
        try {
            const storageKey = currentUserId ? getStorageKey('time_range', currentUserId) : STORAGE_KEY_TIME_RANGE;
            const savedTimeRange = localStorage.getItem(storageKey);
            return savedTimeRange ? JSON.parse(savedTimeRange) : "Year";
        } catch (e) {
            console.error("Error loading time range:", e);
            return "Year";
        }
    });
    
    // Load custom date range from localStorage
    const [localCustomDateRange, setLocalCustomDateRange] = useState(() => {
        try {
            const storageKey = currentUserId ? getStorageKey('custom_date', currentUserId) : STORAGE_KEY_CUSTOM_DATE;
            const savedCustomDate = localStorage.getItem(storageKey);
            if (savedCustomDate) {
                const parsed = JSON.parse(savedCustomDate);
                return [{
                    startDate: parsed.startDate ? new Date(parsed.startDate) : null,
                    endDate: parsed.endDate ? new Date(parsed.endDate) : null,
                    key: 'selection'
                }];
            }
        } catch (e) {
            console.error("Error loading custom date:", e);
        }
        return [{ startDate: null, endDate: null, key: 'selection' }];
    });
    
    const [localIsCustomDateRange, setLocalIsCustomDateRange] = useState(() => {
        try {
            const storageKey = currentUserId ? getStorageKey('custom_date', currentUserId) : STORAGE_KEY_CUSTOM_DATE;
            const savedCustomDate = localStorage.getItem(storageKey);
            if (savedCustomDate) {
                const parsed = JSON.parse(savedCustomDate);
                return parsed.isCustom || false;
            }
        } catch (e) {
            console.error("Error loading isCustomDateRange:", e);
        }
        return false;
    });

    const [localAllBins, setLocalAllBins] = useState([]);
    const [localWasteTypeDropdownOpen, setLocalWasteTypeDropdownOpen] = useState(false);
    const [localSelectedWasteTypes, setLocalSelectedWasteTypes] = useState(['organic']);
    const [messages, setMessages] = useState([]);
    const [wasteCreatedKg, setWasteCreatedKg] = useState(0);

    // New state for bin type tracking
    const [binTypes, setBinTypes] = useState({});
    const [hasGarbyVisionBins, setHasGarbyVisionBins] = useState(false);
    
    // Track if we've initialized bins
    const hasInitializedBins = useRef(false);

    const {t} = useTranslation();

    // Hooks
    const location = useLocation();
    const navigate = useNavigate();

    // Context values
    const {
        selectedTimeRange: contextTimeRange,
        selectedBins: contextBins,
        userRole,
        selectedWasteTypes: contextWasteTypes,
        wasteTypeOptions: contextWasteTypeOptions,
        customDateRange: contextCustomDateRange,
        isCustomDateRange: contextIsCustomDateRange,
    } = useFilters();

    // Derived values - use persistent bins
    const selectedBins = contextBins || persistentSelectedBins;
    const selectedTimeRange = contextTimeRange || localSelectedTimeRange;
    const timeRangeOptions = ["Day", "Week", "Month", "3 Month", "6 Month", "Year"];
    const selectedWasteTypes = contextWasteTypes || localSelectedWasteTypes;
    const wasteTypeOptions = contextWasteTypeOptions || ['pmd', 'organic', 'paper', 'residual'];

    // Use local custom date range
    const customDateRange = contextCustomDateRange || localCustomDateRange;
    const isCustomDateRange = contextIsCustomDateRange || localIsCustomDateRange;

    // ✅ PERSIST: Save timeRange to localStorage whenever it changes
    useEffect(() => {
        if (!contextTimeRange) {
            const storageKey = currentUserId ? getStorageKey('time_range', currentUserId) : STORAGE_KEY_TIME_RANGE;
            localStorage.setItem(storageKey, JSON.stringify(localSelectedTimeRange));
        }
    }, [localSelectedTimeRange, contextTimeRange, currentUserId]);

    // ✅ PERSIST: Save custom date range to localStorage whenever it changes
    useEffect(() => {
        if (!contextCustomDateRange) {
            const storageKey = currentUserId ? getStorageKey('custom_date', currentUserId) : STORAGE_KEY_CUSTOM_DATE;
            const toSave = {
                startDate: localCustomDateRange[0]?.startDate
                    ? (localCustomDateRange[0].startDate instanceof Date
                        ? localCustomDateRange[0].startDate.toISOString()
                        : localCustomDateRange[0].startDate)
                    : null,
                endDate: localCustomDateRange[0]?.endDate
                    ? (localCustomDateRange[0].endDate instanceof Date
                        ? localCustomDateRange[0].endDate.toISOString()
                        : localCustomDateRange[0].endDate)
                    : null,
                isCustom: localIsCustomDateRange
            };
            localStorage.setItem(storageKey, JSON.stringify(toSave));
        }
    }, [localCustomDateRange, localIsCustomDateRange, contextCustomDateRange, currentUserId]);

    // ✅ HANDLER: called by SiteHeader when custom date is picked or reset
    const handleCustomDateRangeChange = (newDateRange, isCustom) => {
        setLocalCustomDateRange(newDateRange);
        setLocalIsCustomDateRange(isCustom);
        // If resetting to a standard range, clear the custom date from storage
        if (!isCustom) {
            const storageKey = currentUserId ? getStorageKey('custom_date', currentUserId) : STORAGE_KEY_CUSTOM_DATE;
            localStorage.removeItem(storageKey);
        }
    };

    // Function to fetch bin types
    const fetchBinTypes = async (bins) => {
        if (!bins || bins.length === 0) {
            setHasGarbyVisionBins(false);
            setBinTypes({});
            return;
        }

        try {
            const response = await axiosInstance.get('/get_bins');
            const allBins = response.data;

            const binTypeMap = {};
            let garbyVisionFound = false;

            bins.forEach(binId => {
                const bin = allBins.find(b => b.unique_identifier === binId);
                if (bin) {
                    binTypeMap[binId] = {
                        isGarbyVision: bin.is_garby_vision,
                        isGarfill: bin.is_garfill,
                        isWeightScale: bin.is_weight_scale
                    };

                    if (bin.is_garby_vision) {
                        garbyVisionFound = true;
                    }
                }
            });

            setBinTypes(binTypeMap);
            setHasGarbyVisionBins(garbyVisionFound);

        } catch (error) {
            console.error("Error fetching bin types:", error);
            setHasGarbyVisionBins(false);
            setBinTypes({});
        }
    };

    // Check bin types when selected bins change
    useEffect(() => {
        fetchBinTypes(selectedBins);
    }, [selectedBins]);

    // Initialize user role from localStorage
    useEffect(() => {
        const savedRole = localStorage.getItem("userRole");
        if (savedRole) {
            setUserRoleState(savedRole);
            setIsLoadingRole(false);
        }
    }, []);

    // Push notifications initialization
    useEffect(() => {
        let unsubscribe = null;

        const initializeNotifications = async () => {
            try {
                if (!('serviceWorker' in navigator) || !('Notification' in window)) {
                    console.log('Service Worker or Notifications not supported');
                    return;
                }

                const permission = await Notification.requestPermission();
                if (permission !== 'granted') {
                    console.log('Notification permission denied');
                    return;
                }

                const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

                const currentToken = await getToken(messaging, {
                    serviceWorkerRegistration: swRegistration
                });

                if (currentToken) {
                    console.log('Registration token:', currentToken);
                    await sendTokenToServer(currentToken);
                }
            } catch (error) {
                console.error('Error initializing notifications:', error);
            }
        };

        initializeNotifications();

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [navigate]);

    const sendTokenToServer = async (token) => {
        try {
            const csrfResponse = await axiosInstance.get("csrf/");
            const csrfToken = csrfResponse.data.csrfToken;

            const response = await axiosInstance.post('/fcm/devices/', {
                registration_id: token,
                type: 'web'
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                withCredentials: true,
            });

            if (response.status === 200 || response.status === 201) {
                console.log('Token sent to server successfully');
            } else {
                console.error('Failed to send token to server');
            }
        } catch (error) {
            console.error('Error sending token to server:', error);
        }
    };

    // Fetch user role if not provided by context
    useEffect(() => {
        if (userRole) {
            setUserRoleState(userRole);
            localStorage.setItem("userRole", userRole);
            setIsLoadingRole(false);
        } else if (!userRoleState) {
            const fetchUserRole = async () => {
                try {
                    const response = await axiosInstance.get("usertier");
                    const fetchedRole = response.data;
                    setUserRoleState(fetchedRole);
                    localStorage.setItem("userRole", fetchedRole);
                } catch (error) {
                    console.error("Error fetching user role:", error);
                } finally {
                    setIsLoadingRole(false);
                }
            };
            fetchUserRole();
        }
    }, [userRole, userRoleState]);

    // Check for bin images when selected bins change
    useEffect(() => {
        const checkForBinImages = async () => {
            if (selectedBins.length === 1) {
                try {
                    const response = await axiosInstance.get(`/bin-media/?bin_id=${selectedBins[0]}`);
                    setHasBinImages(response.data.length > 0);
                } catch (error) {
                    console.error("Error checking for bin images:", error);
                    setHasBinImages(false);
                }
            } else {
                setHasBinImages(false);
            }
        };

        checkForBinImages();
    }, [selectedBins]);

    // ✅ FIXED: Fetch bin IDs only once and respect persisted selection
    useEffect(() => {
        if (!contextBins && !hasInitializedBins.current) {
            const fetchBinIds = async () => {
                try {
                    const response = await axiosInstance.get("bins-with-status");
                    const binsWithStatus = response.data || [];
                    const binIds = binsWithStatus.map(bin => bin.id);
                    const validBinIds = binIds.filter((id) => id && typeof id === "string");

                    // Only set default if we have NO persisted selection at all
                    if (persistentSelectedBins.length === 0) {
                        // Use first 3 bins or all if less than 3
                        const defaultSelection = validBinIds.slice(0, Math.min(3, validBinIds.length));
                        persistentUpdateSelectedBins(defaultSelection);
                    } else {
                        // Filter persisted bins to only include valid ones
                        const validPersistedBins = persistentSelectedBins.filter(id => validBinIds.includes(id));
                        
                        // Only update if we lost some bins (they're no longer valid)
                        if (validPersistedBins.length < persistentSelectedBins.length) {
                            console.log("Some saved bins are no longer valid, updating selection");
                            persistentUpdateSelectedBins(validPersistedBins);
                        }
                        // If all persisted bins are still valid, DO NOTHING - keep the selection
                    }

                    setLocalAllBins(binsWithStatus);
                    hasInitializedBins.current = true; // Mark as initialized
                    
                } catch (error) {
                    console.error("Error fetching bin IDs:", error);
                    try {
                        const fallbackResponse = await axiosInstance.get("bin_ids");
                        const binIds = fallbackResponse.data || [];
                        const validBinIds = binIds.filter((id) => id && typeof id === "string");
                        setLocalAllBins(validBinIds.map(id => ({id, status: 'offline'})));
                        
                        if (persistentSelectedBins.length === 0) {
                            const defaultSelection = validBinIds.slice(0, Math.min(3, validBinIds.length));
                            persistentUpdateSelectedBins(defaultSelection);
                        }
                        hasInitializedBins.current = true;
                    } catch (fallbackError) {
                        console.error("Error with fallback bin fetch:", fallbackError);
                        hasInitializedBins.current = true;
                    }
                }
            };

            fetchBinIds();
        }
    }, [contextBins, persistentSelectedBins, persistentUpdateSelectedBins]);

    // Bin usage stats
    useEffect(() => {
        const fetchBinUsageIds = async () => {
            try {
                const queryParams = selectedBins.length > 0
                    ? `?bins=${selectedBins.join(',')}`
                    : '';
                const response = await axiosInstance.get(`bin_usage_ids${queryParams}`, {
                    withCredentials: true,
                });
                const nonGarbyVisionBins = response.data || [];
                setBinUsageIds(nonGarbyVisionBins);
                setShowBinUsageStats(nonGarbyVisionBins.length > 0);
            } catch (error) {
                console.error("Error fetching bin IDs:", error);
                setShowBinUsageStats(false);
                setBinUsageIds([]);
            }
        };

        if (selectedBins.length > 0) {
            fetchBinUsageIds();
        } else {
            setShowBinUsageStats(false);
            setBinUsageIds([]);
        }
    }, [selectedBins]);

    useEffect(() => {
        const refreshBinStatuses = async () => {
            try {
                const response = await axiosInstance.get("bins-with-status");
                const binsWithStatus = response.data || [];
                setLocalAllBins(binsWithStatus);
            } catch (error) {
                console.error("Error refreshing bin statuses:", error);
            }
        };

        const interval = setInterval(refreshBinStatuses, 60000);
        return () => clearInterval(interval);
    }, []);

    const selectLocalTimeRange = (timeRange) => {
        setLocalSelectedTimeRange(timeRange);
        return false;
    };

    const toggleLocalWasteTypeSelection = (wasteType) => {
        if (localSelectedWasteTypes.includes(wasteType)) {
            setLocalSelectedWasteTypes(localSelectedWasteTypes.filter((item) => item !== wasteType));
        } else {
            setLocalSelectedWasteTypes([...localSelectedWasteTypes, wasteType]);
        }
        return false;
    };

    const handleUploadSuccess = () => {
        setRefreshKey((prev) => prev + 1);
    };

    const currentWasteStream = selectedWasteTypes.length > 0
        ? selectedWasteTypes[0]
        : 'organic';

    return (
        <DashboardExport>
            <div className="export-wrapper">
                <div className="tab-content h-full">
                    <div className="w-full max-w-[1920px] sm:px-4 m-auto sm:pt-0">

                        {!contextTimeRange && (
                            <div
                                className="sticky top-16 pt-4 pb-6 text-right z-10 w-full ml-auto px-4 sm:pr-0 bg-gradient-to-t from-white/0 to-white dark:from-slate-950/0 dark:to-slate-950 flex gap-2 justify-end">
                                <SiteHeader
                                    timeRangeDropdownOpen={localTimeRangeDropdownOpen}
                                    setTimeRangeDropdownOpen={setLocalTimeRangeDropdownOpen}
                                    binDropdownOpen={localBinDropdownOpen}
                                    setBinDropdownOpen={setLocalBinDropdownOpen}
                                    selectedTimeRange={localSelectedTimeRange}
                                    selectTimeRange={selectLocalTimeRange}
                                    timeRangeOptions={timeRangeOptions}
                                    selectedBins={selectedBins}
                                    allBins={localAllBins}
                                    toggleBinSelection={persistentToggleBinSelection}
                                    wasteTypeDropdownOpen={localWasteTypeDropdownOpen}
                                    setWasteTypeDropdownOpen={setLocalWasteTypeDropdownOpen}
                                    selectedWasteTypes={localSelectedWasteTypes}
                                    toggleWasteTypeSelection={toggleLocalWasteTypeSelection}
                                    wasteTypeOptions={wasteTypeOptions}
                                    customDateRange={localCustomDateRange}
                                    isCustomDateRange={localIsCustomDateRange}
                                    onCustomDateRangeChange={handleCustomDateRangeChange}
                                />
                            </div>
                        )}

                        <div
                            className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-5 gap-6 sm:gap-8 sm:mt-4 font-sans">
                            <div className="mb-6 sm:mb-0 flex flex-col gap-8">
                                <div className="sm:rounded-lg border-t sm:border shadow-sm">
                                    <div className="aspect-[16/10]">
                                        <div className="relative w-full overflow-auto">
                                            <DataTable
                                                className="w-full caption-bottom text-sm aspect-[16/10]"
                                                title={t("filllevellabel")}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="sm:rounded-lg border-t sm:border shadow-sm">
                                   <WasteCreated 
                                            bins={selectedBins} 
                                            timeRange={selectedTimeRange}
                                            onKgWasteUpdate={setWasteCreatedKg}
                                            customDateRange={customDateRange}      
                                            isCustomDateRange={isCustomDateRange}  
                                    />
                                </div>

                                {selectedBins.length > 0 && hasGarbyVisionBins && (
                                    <div className="sm:rounded-lg border-t sm:border shadow-sm bg-muted/50">
                                            <ContaminationChart
                                                title={t('contamination.title')}
                                                timeRange={
                                                    selectedTimeRange.toLowerCase().replace(/\s+/g, "") === "3month"
                                                        ? "3months"
                                                        : selectedTimeRange.toLowerCase().replace(/\s+/g, "") === "6month"
                                                            ? "6months"
                                                            : selectedTimeRange.toLowerCase().replace(/\s+/g, "")
                                                }
                                                bins={selectedBins}
                                                customDateRange={customDateRange}
                                                isCustomDateRange={isCustomDateRange}
                                            />
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-subgrid lg:col-span-2 2xl:col-span-4 content-start gap-1 sm:gap-4 items-start" style={{ gridAutoRows: '1fr' }}>

                                <Stats
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("wastegenerationlabel")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />
                                
                                {selectedBins.length > 0 && (
                                    <div 
                                        className="lg:col-span-2 lg:row-span-2 sm:rounded-lg bg-muted/50 overflow-hidden flex flex-col"
                                        style={{ 
                                            aspectRatio: '16/23'
                                        }}
                                    >
                                        <SubclassCardView
                                            className="w-full h-full"
                                            id={selectedBins}
                                            dayvalue={selectedTimeRange.toLowerCase()}
                                            datevalue={customDateRange}
                                            isCustomDateRange={isCustomDateRange}
                                            wasteweight={wasteCreatedKg}
                                            totalwasteproduceddata={1004}
                                        />
                                    </div>
                                )}

                                {selectedBins.length > 0 && (
                                    <SubclassDisplay
                                        className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                        id={selectedBins}
                                        currentWasteItem={selectedWasteTypes[0] || 'organic'}
                                        currentWasteItem_={selectedWasteTypes[0] || 'organic'}
                                        dayvalue={selectedTimeRange.toLowerCase()}
                                        selectedButton={selectedTimeRange.toLowerCase()}
                                        datevalue={customDateRange}
                                        wasteweight={wasteCreatedKg}
                                        isCustomDateRange={isCustomDateRange}
                                    />
                                )}

                                {selectedBins.length > 0 && (
                                    <BinMediaViewer
                                        key={refreshKey}
                                        selectedBin={selectedBins}
                                        className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    />
                                )}

                                <WasteStreamProduction
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("wastestreamproductionlabel")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <PeakUsageTime
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("peakUsageTime")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <FillLevelConsistency
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("fillLevelConsistency.title")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <BinUsageRanking
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("binUsageRanking.defaultTitle")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />

                                <WasteComposition
                                    className="lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4"
                                    title={t("wastecompositionlabel")}
                                    timeRange={selectedTimeRange}
                                    bins={selectedBins}
                                    customDateRange={customDateRange}
                                    isCustomDateRange={isCustomDateRange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DashboardExport>
    );
}